/**
 * Single source of truth for per-host, per-route page metadata.
 *
 * Imported by BOTH:
 *   - frontend/src/components/SeoManager.jsx (client, after React mounts)
 *   - middleware.js at the repo root (Vercel edge, before the HTML is served)
 *
 * That dual use is the whole point: the app is a client-rendered CRA bundle with
 * one static shell for every hostname, so anything that only runs in SeoManager
 * is invisible to crawlers that do not execute JS. Keep this module pure — no
 * DOM, no `window`, no `@/` path aliases, no React — or the edge bundle breaks.
 */

export const SITE_NAME = "Ordrs";
export const SITE_URL = "https://www.ordrs.ai";
export const DEFAULT_IMAGE = `${SITE_URL}/social/ordrsai/slide1.png`;

/**
 * Hostnames the platform serves, and the canonical origin for each.
 * Anything not listed here (merchant-owned custom storefront domains,
 * *.vercel.app previews) is deliberately left alone.
 */
export const HOST_ORIGINS = {
  "ordrs.ai": SITE_URL,
  "www.ordrs.ai": SITE_URL,
  "auto.ordrs.ai": "https://auto.ordrs.ai",
  "merchant.ordrs.ai": "https://merchant.ordrs.ai",
  "provider.ordrs.ai": "https://provider.ordrs.ai",
  "autoaccount.ordrs.ai": "https://autoaccount.ordrs.ai",
  "account.ordrs.ai": "https://autoaccount.ordrs.ai",
};

/**
 * What "/" means on each host. Mirrors rootRouteForHost() and the isAutoHost
 * branch in frontend/src/App.js — if the routing there changes, change this.
 */
export const HOST_ROOT_ROUTE = {
  "auto.ordrs.ai": "/auto",
  "merchant.ordrs.ai": "/merchant",
  "provider.ordrs.ai": "/provider",
  "autoaccount.ordrs.ai": "/autoaccount",
  "account.ordrs.ai": "/autoaccount",
};

export const NO_INDEX_PREFIXES = [
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/merchant",
  "/provider",
  "/autoaccount",
  "/account",
  "/preview/",
  "/build/",
  "/onboard/",
  "/t/",
  "/s/",
  "/dashboard/",
  "/kitchen/",
  "/fulfillment/",
  "/phone-sim/",
  "/subscribe",
];

const AUTO_SEO = {
  title: "Auto Shop Job Intake and AI Phone Workflow",
  description:
    "Ordrs for auto helps independent repair shops capture missed calls, collect vehicle details up front, turn calls into service requests, and pilot an AI phone and workflow system built for automotive operations.",
  keywords:
    "auto shop AI phone, auto shop job intake, mechanic shop missed calls, brake shop booking, tire shop service requests, oil change shop software",
};

export const ROUTE_SEO = {
  "/": {
    title: "AI Phone Host and Operator Workflow Software",
    description: "Ordrs gives small businesses an AI phone host, direct ordering or booking, and an operator dashboard where phone, web, and booking activity land in one workflow.",
    keywords: "AI phone ordering, AI booking software, operator workflow software, restaurant ordering software, small business phone agent, direct online ordering, appointment booking software",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Ordrs",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: SITE_URL,
      description: "AI phone host and operator workflow software for restaurants, retailers, and service businesses.",
      offers: {
        "@type": "Offer",
        priceCurrency: "USD",
        price: "179",
      },
      publisher: {
        "@type": "Organization",
        name: "Ordrs",
        url: SITE_URL,
      },
    },
  },
  "/pricing": {
    title: "Pricing for AI Ordering and Booking Software",
    description: "See Ordrs pricing for storefronts, AI phone ordering, appointment booking, and multi-location operations. Flat plans with direct Stripe payouts.",
    keywords: "Ordrs pricing, AI ordering software pricing, restaurant online ordering pricing, AI phone receptionist pricing",
  },
  "/solutions": {
    title: "AI Storefront, Phone Host, Booking, and Operations Platform",
    description: "Explore Ordrs features: AI storefront generation, AI phone host, direct checkout, booking flows, multi-location operations, and business advisor tools.",
    keywords: "AI storefront generator, AI phone host, booking software, direct ordering system, restaurant operations software",
  },
  "/auto": AUTO_SEO,
  "/auto-repair": AUTO_SEO,
  "/about": {
    title: "About Ordrs",
    description: "Learn how Ordrs helps independent restaurants, retail shops, and service businesses own their customer flow with direct ordering, booking, and AI phone support.",
    keywords: "about Ordrs, AI phone ordering company, small business software platform",
  },
  "/contact": {
    title: "Contact Ordrs",
    description: "Contact Ordrs for demos, pricing questions, onboarding, or custom setup help for ordering, booking, and AI phone workflows.",
    keywords: "contact Ordrs, Ordrs demo, Ordrs support",
  },
  "/faq": {
    title: "FAQ for Ordering, Booking, Billing, and AI Phone Setup",
    description: "Read the Ordrs FAQ covering billing, setup, AI phone host behavior, ordering, booking, storefronts, and account operations.",
    keywords: "Ordrs FAQ, AI phone host FAQ, online ordering FAQ, booking software FAQ",
  },
  "/waitlist": {
    title: "Join the Ordrs Waitlist",
    description: "Join the Ordrs waitlist for early access to the AI assistant, AI phone host, ordering, booking, and operator workflow tools.",
    keywords: "Ordrs waitlist, AI assistant waitlist, AI phone software early access",
  },
  "/partners": {
    title: "Affiliate and Partner Program",
    description: "Apply for the Ordrs affiliate and partner program. Earn recurring commission for referring restaurants, retailers, and service businesses to Ordrs.",
    keywords: "Ordrs affiliate program, restaurant software affiliate, SaaS partner program",
  },
  "/docs": {
    title: "Public Documentation",
    description: "Read Ordrs public documentation for merchant setup, service-provider setup, and AI host guidance.",
    keywords: "Ordrs docs, merchant setup guide, service provider setup guide, AI host guide",
  },
  "/docs/merchants": {
    title: "Merchant Setup Guide",
    description: "Set up Ordrs for order-taking businesses like bakeries, cafes, florists, delis, and retail shops.",
    keywords: "merchant setup guide, restaurant setup guide, retail ordering software setup",
  },
  "/docs/service-providers": {
    title: "Service Provider Setup Guide",
    description: "Set up Ordrs for salons, spas, clinics, therapists, barbers, and other appointment-based businesses.",
    keywords: "service provider setup guide, salon booking software, clinic booking setup",
  },
  "/docs/ai-host": {
    title: "AI Host and Advisor Guide",
    description: "Understand how the Ordrs AI phone host, FAQ handling, and screen-aware assistant work together in live operations.",
    keywords: "AI phone host guide, AI receptionist guide, operator assistant software",
  },
  "/demo": {
    title: "Live Operator Workflow Demo",
    description: "Drive the Ordrs demo: a generated storefront, AI phone host, direct order and booking intake, and one operator queue for phone, web, and service activity.",
    keywords: "Ordrs demo, AI phone host demo, operator workflow demo, AI ordering demo, booking software demo",
  },
  "/gallery": {
    title: "Storefront Gallery",
    description: "Browse example storefronts and visual directions generated with Ordrs for restaurants, retailers, and service businesses.",
    keywords: "storefront gallery, AI storefront examples, Ordrs website examples",
  },

  // Account-launcher surfaces. All noindex — titles exist so the browser tab and
  // any internal link preview are honest, not so they rank.
  "/merchant": {
    title: "Merchant Account Launcher",
    description: "Set up or sign in to an Ordrs merchant account for ordering, storefronts, and AI phone coverage.",
    keywords: "",
  },
  "/provider": {
    title: "Service Provider Account Launcher",
    description: "Set up or sign in to an Ordrs service-provider account for booking, appointments, and AI phone coverage.",
    keywords: "",
  },
  "/account": {
    title: "Account Portal",
    description: "Sign in to your Ordrs account portal.",
    keywords: "",
  },
  "/autoaccount": {
    title: "Auto Repair Account Launcher",
    description: "Set up or sign in to an Ordrs auto repair account for service intake, work orders, invoices, and AI service-advisor coverage.",
    keywords: "",
  },
};

export function normalizeHost(host) {
  return (host || "").split(":")[0].trim().toLowerCase();
}

export function isPlatformHost(host) {
  return Object.prototype.hasOwnProperty.call(HOST_ORIGINS, normalizeHost(host));
}

export function stripTrailingSlash(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

export function isNoIndexPath(pathname) {
  return NO_INDEX_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix));
}

/**
 * Resolve the full metadata set for a host + path.
 * Returns null for hosts we do not own, so callers can pass through untouched.
 */
export function resolveSeo(host, pathname) {
  const h = normalizeHost(host);
  const origin = HOST_ORIGINS[h];
  if (!origin) return null;

  let normalized = stripTrailingSlash(pathname || "/");

  // On a subdomain, "/" renders that subdomain's page — resolve its metadata,
  // but keep the canonical URL pointing at "/" where the user actually is.
  const isRoot = normalized === "/";
  const effective = isRoot && HOST_ROOT_ROUTE[h] ? HOST_ROOT_ROUTE[h] : normalized;

  let entry = ROUTE_SEO[effective];
  if (!entry && effective.startsWith("/docs/")) entry = ROUTE_SEO["/docs"];
  if (!entry) entry = ROUTE_SEO["/"];

  const canonicalPath = isRoot ? "" : normalized;
  const canonicalUrl = `${origin}${canonicalPath}`;
  const title = `${entry.title} | ${SITE_NAME}`;
  const noIndex = isNoIndexPath(effective);

  return {
    host: h,
    origin,
    pathname: normalized,
    effectivePath: effective,
    title,
    description: entry.description,
    keywords: entry.keywords || "",
    image: DEFAULT_IMAGE,
    canonicalUrl,
    noIndex,
    jsonLd: entry.jsonLd || {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      description: entry.description,
      url: canonicalUrl,
    },
  };
}
